// Settings → Quick links: five dropdown slots for the header's top-right
// buttons. Home is always first and not editable. Each slot lists every link
// not already used in another slot, plus "None". Dropdowns expand inline
// (no nested Modal inside the Settings sheet on RN-web).
import React, { useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View } from 'react-native';
import { ChevronDown, ChevronUp } from 'lucide-react-native';
import { makeThemedStyles, useV10 } from '../../v10/theme';
import {
  DEFAULT_QUICK_LINKS,
  MAX_QUICK_LINKS,
  QUICK_LINK_OPTIONS,
  quickLinkLabel,
  setQuickLinks,
  useQuickLinks,
} from '../quickLinks';

export function QuickLinksEditor() {
  const t = useV10();
  const styles = useStyles();
  const slots = useQuickLinks();
  const [openSlot, setOpenSlot] = useState<number | null>(null);

  const choose = (slot: number, id: string | null) => {
    const next = [...slots];
    next[slot] = id;
    setQuickLinks(next);
    setOpenSlot(null);
  };

  return (
    <View>
      <Text style={styles.hint}>
        The buttons at the top right. Home is always first; pick up to {MAX_QUICK_LINKS} more.
      </Text>
      <View style={styles.row}>
        <Text style={styles.slotLabel}>Fixed</Text>
        <View style={[styles.select, styles.selectFixed]}>
          <Text style={styles.selectText}>Home</Text>
        </View>
      </View>
      {Array.from({ length: MAX_QUICK_LINKS }, (_, i) => {
        const value = slots[i];
        const isOpen = openSlot === i;
        const usedElsewhere = new Set(slots.filter((s, j) => s && j !== i));
        const options = QUICK_LINK_OPTIONS.filter((o) => !usedElsewhere.has(o.id));
        const Chevron = isOpen ? ChevronUp : ChevronDown;
        return (
          <View key={i}>
            <View style={styles.row}>
              <Text style={styles.slotLabel}>Link {i + 1}</Text>
              <Pressable
                style={styles.select}
                onPress={() => setOpenSlot(isOpen ? null : i)}
                accessibilityRole="button"
                accessibilityLabel={`Quick link ${i + 1}: ${value ? quickLinkLabel(value) : 'None'}`}
              >
                <Text style={[styles.selectText, !value && styles.selectTextEmpty]}>
                  {value ? quickLinkLabel(value) : 'None'}
                </Text>
                <Chevron size={16} color={t.ivorySoft} />
              </Pressable>
            </View>
            {isOpen ? (
              <View style={styles.menu}>
                <ScrollView style={{ maxHeight: 240 }} nestedScrollEnabled>
                  <Option label="None" active={!value} onPress={() => choose(i, null)} />
                  {options.map((o) => (
                    <Option key={o.id} label={o.label} active={o.id === value} onPress={() => choose(i, o.id)} />
                  ))}
                </ScrollView>
              </View>
            ) : null}
          </View>
        );
      })}
      <Pressable onPress={() => setQuickLinks(DEFAULT_QUICK_LINKS)} hitSlop={6} style={styles.reset}>
        <Text style={styles.resetText}>Reset to SyQi + HRMEZ</Text>
      </Pressable>
    </View>
  );
}

function Option({ label, active, onPress }: { label: string; active: boolean; onPress: () => void }) {
  const styles = useStyles();
  return (
    <Pressable onPress={onPress} style={({ pressed }) => [styles.option, active && styles.optionActive, pressed && { opacity: 0.7 }]}>
      <Text style={[styles.optionText, active && styles.optionTextActive]}>{label}</Text>
    </Pressable>
  );
}

const useStyles = makeThemedStyles(({ t }) =>
  StyleSheet.create({
    hint: { fontSize: 12, color: t.ivorySoft, marginTop: 6, marginBottom: 4, fontStyle: 'italic' },
    row: { flexDirection: 'row', alignItems: 'center', gap: 10, marginTop: 8 },
    slotLabel: { width: 56, fontSize: 13, fontWeight: '700', color: t.ivory },
    select: {
      flex: 1,
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 12,
      paddingVertical: 10,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: t.glass,
    },
    selectFixed: { opacity: 0.6 },
    selectText: { fontSize: 14, fontWeight: '700', color: t.ivory },
    selectTextEmpty: { color: t.ivorySoft, fontWeight: '500' },
    menu: {
      marginTop: 4,
      marginLeft: 66,
      borderRadius: 10,
      borderWidth: 1,
      borderColor: t.navyLine,
      backgroundColor: t.glass,
      overflow: 'hidden',
    },
    option: { paddingHorizontal: 12, paddingVertical: 9 },
    optionActive: { backgroundColor: t.gold },
    optionText: { fontSize: 14, color: t.ivory },
    optionTextActive: { color: t.onGold, fontWeight: '800' },
    reset: { alignSelf: 'flex-start', marginTop: 10 },
    resetText: { fontSize: 12, color: t.goldBright, fontWeight: '700' },
  }),
);
